import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { isAuthenticated, user } from '@stores/authStore';
import { api } from '@services/api';
import { getStatusLabel, getPriorityLabel } from '@utils/labels';
import { IssueLinkSelector } from '@components/common/IssueLinkSelector';

interface Issue {
  id: number;
  project_id: number;
  project_code: string;
  issue_number: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  is_hidden?: boolean;
  reporter_name: string;
  reporter_id?: number;
  assignee_name: string;
  assignee_id?: number;
  rejected_at?: string;
  rejected_by?: { id: number; name: string };
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
}

interface Comment {
  id: number;
  content: string;
  user_name: string;
  created_at: string;
  images?: CommentImage[];
}

interface CommentImage {
  id: number;
  image_path: string;
  uploaded_at: string;
}

interface IssueImage {
  id: number;
  image_path: string;
  uploaded_at: string;
}

interface IssueAttachment {
  id: number;
  file_name: string;
  file_size: number;
  mime_type: string;
  uploaded_at: string;
}

interface LinkedIssue {
  id: number;
  issue_number: string;
  title: string;
  status: string;
}

interface Commit {
  id: number;
  commit_hash: string;
  commit_url?: string;
  author: string;
  message: string;
  committed_at: string;
}

interface LinkedWikiPage {
  id: number;
  title: string;
  content: string;
  created_at: string;
  updated_at: string | null;
}

interface IssueDetailPageProps {
  id: string;
}

interface User {
  id: number;
  name: string;
  email: string;
}

export function IssueDetailPage({ id }: IssueDetailPageProps) {
  const [issue, setIssue] = useState<Issue | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [images, setImages] = useState<IssueImage[]>([]);
  const [attachments, setAttachments] = useState<IssueAttachment[]>([]);
  const [linkedIssues, setLinkedIssues] = useState<LinkedIssue[]>([]);
  const [commits, setCommits] = useState<Commit[]>([]);
  const [linkedWikiPages, setLinkedWikiPages] = useState<LinkedWikiPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [commentImages, setCommentImages] = useState<FileList | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [projectMembers, setProjectMembers] = useState<User[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>('');
  const [showAddCommitForm, setShowAddCommitForm] = useState(false);
  const [commitUrl, setCommitUrl] = useState('');
  const [addingCommit, setAddingCommit] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isDraggingCommentImage, setIsDraggingCommentImage] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editAssigneeId, setEditAssigneeId] = useState<string>('');
  const [editIsHidden, setEditIsHidden] = useState(false);
  const [editLinkedIssues, setEditLinkedIssues] = useState<string[]>([]);
  const [editAttachments, setEditAttachments] = useState<FileList | null>(null);

  useEffect(() => {
    if (!isAuthenticated.value) {
      route('/login');
      return;
    }

    loadIssueData();
  }, [id]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedImage) {
        setSelectedImage(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedImage]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showEditModal || showRejectModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showEditModal, showRejectModal]);

  // Add paste event listener for images
  useEffect(() => {
    document.addEventListener('paste', handleImagePaste as any);
    return () => document.removeEventListener('paste', handleImagePaste as any);
  }, [id]);

  const loadIssueData = async () => {
    setLoading(true);
    await Promise.all([
      loadIssue(),
      loadComments(),
      loadImages(),
      loadAttachments(),
      loadCommits(),
      loadLinkedWikiPages(),
    ]);
    setLoading(false);
  };

  const loadIssue = async () => {
    const response = await api.get<any>(`/api/issues/${id}`);
    if (response.success && response.data) {
      const issueData = response.data;
      setIssue({
        ...issueData,
        reporter_name: issueData.reporter?.name || '알 수 없음',
        reporter_id: issueData.reporter?.id,
        assignee_name: issueData.assignee?.name || '',
        assignee_id: issueData.assignee?.id,
      });

      // Set linked issues
      setLinkedIssues(issueData.linked_issues || []);

      // Load project members to get user role
      if (issueData.project_id) {
        const membersResponse = await api.get<any>(`/api/projects/${issueData.project_id}/members`);
        if (membersResponse.success && membersResponse.data) {
          const members = membersResponse.data.members || [];
          const currentMember = members.find((m: any) => m.user_id === user.value?.id);
          if (currentMember) {
            setUserRole(currentMember.role);
          } else if (user.value?.is_admin) {
            setUserRole('pm');
          }
        }
      }
    }
  };

  const loadProjectMembers = async (projectId: number) => {
    const response = await api.get<any>(`/api/projects/${projectId}/members`);
    if (response.success && response.data) {
      setProjectMembers(response.data.members || []);
    }
  };

  const loadComments = async () => {
    const response = await api.get<any>(`/api/issues/${id}/comments`);
    if (response.success && response.data) {
      const commentsData = response.data.items || [];

      // Load images for each comment
      const commentsWithImages = await Promise.all(
        commentsData.map(async (comment: Comment) => {
          const imgResponse = await api.get<any>(`/api/comments/${comment.id}/images`);
          if (imgResponse.success && imgResponse.data) {
            return { ...comment, images: imgResponse.data.images || [] };
          }
          return { ...comment, images: [] };
        })
      );

      setComments(commentsWithImages);
    }
  };

  const loadImages = async () => {
    const response = await api.get<any>(`/api/issues/${id}/images`);
    if (response.success && response.data) {
      setImages(response.data.images || []);
    }
  };

  const loadAttachments = async () => {
    const response = await api.get<any>(`/api/issues/${id}/attachments`);
    if (response.success && response.data) {
      setAttachments(response.data.attachments || []);
    }
  };

  const loadCommits = async () => {
    const response = await api.get<any>(`/api/commits?issue_id=${id}`);
    if (response.success && response.data) {
      setCommits(response.data.items || []);
    }
  };

  const loadLinkedWikiPages = async () => {
    const response = await api.get<any>(`/api/issues/${id}/wiki`);
    if (response.success && response.data) {
      setLinkedWikiPages(response.data || []);
    }
  };

  const handleFileUpload = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const response = await api.uploadFile(`/api/issues/${id}/attachments`, file);
    if (response.success) {
      loadAttachments();
      input.value = '';
    } else {
      alert(response.error || '파일 업로드에 실패했습니다');
    }
  };

  const handleFileDownload = async (attachmentId: number, fileName: string) => {
    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8100';
      const response = await fetch(`${apiUrl}/api/issues/attachments/${attachmentId}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('파일 다운로드에 실패했습니다');
    }
  };

  const handleFileDelete = async (attachmentId: number) => {
    if (!confirm('첨부파일을 삭제하시겠습니까?')) return;

    const response = await api.delete(`/api/issues/attachments/${attachmentId}`);
    if (response.success) {
      loadAttachments();
    } else {
      alert(response.error || '파일 삭제에 실패했습니다');
    }
  };

  const handleAddComment = async (e: Event) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    const response = await api.post<any>(`/api/comments`, {
      issue_id: parseInt(id),
      content: commentText.trim(),
    });

    if (response.success) {
      const commentId = response.data.id;

      // Upload images if selected
      if (commentImages && commentImages.length > 0) {
        for (let i = 0; i < commentImages.length; i++) {
          await api.uploadFile(`/api/comments/${commentId}/images`, commentImages[i]);
        }
      }

      setCommentText('');
      setCommentImages(null);
      // Reset file input
      const fileInput = document.getElementById('comment-images') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      loadComments();
    } else {
      alert(response.error || 'Failed to add comment');
    }
  };

  const handleImageUpload = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    setUploadingImage(true);

    const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8100';

    for (const file of Array.from(files)) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${API_URL}/api/issues/${id}/images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }
      } catch (error) {
        alert(`Failed to upload ${file.name}`);
      }
    }

    setUploadingImage(false);
    input.value = '';
    loadImages();
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!confirm('이미지를 삭제하시겠습니까?')) {
      return;
    }

    const response = await api.delete<any>(`/api/issues/${id}/images/${imageId}`);
    if (response.success) {
      loadImages();
    } else {
      alert(response.error || '이미지 삭제에 실패했습니다');
    }
  };

  const openEditModal = () => {
    if (!issue) return;

    setEditTitle(issue.title);
    setEditDescription(issue.description || '');
    setEditStatus(issue.status);
    setEditPriority(issue.priority);
    setEditAssigneeId(issue.assignee_id ? String(issue.assignee_id) : '');
    setEditIsHidden(issue.is_hidden || false);
    setEditLinkedIssues(linkedIssues.map(li => li.issue_number));
    setShowEditModal(true);

    if (issue.project_id) {
      loadProjectMembers(issue.project_id);
    }
  };

  const handleEditIssue = async (e: Event) => {
    e.preventDefault();

    if (!editTitle.trim()) {
      alert('제목을 입력해주세요');
      return;
    }

    const payload: any = {
      title: editTitle.trim(),
      description: editDescription.trim(),
      status: editStatus,
      priority: editPriority,
      is_hidden: editIsHidden,
    };

    if (editAssigneeId) {
      payload.assignee_id = parseInt(editAssigneeId);
    }

    // Process linked issues
    payload.linked_issue_numbers = editLinkedIssues;

    const response = await api.put<any>(`/api/issues/${id}`, payload);
    if (response.success) {
      // Upload attachments if selected
      if (editAttachments && editAttachments.length > 0) {
        for (let i = 0; i < editAttachments.length; i++) {
          await api.uploadFile(`/api/issues/${id}/attachments`, editAttachments[i]);
        }
      }

      setShowEditModal(false);
      setEditAttachments(null);
      loadIssue();
      loadAttachments();
    } else {
      alert(response.error || '이슈 수정에 실패했습니다');
    }
  };

  const handleRejectIssue = async (e: Event) => {
    e.preventDefault();

    if (!rejectionReason.trim()) {
      alert('거절 사유를 입력해주세요');
      return;
    }

    const response = await api.post<any>(`/api/issues/${id}/reject`, {
      rejection_reason: rejectionReason.trim(),
    });

    if (response.success) {
      setShowRejectModal(false);
      setRejectionReason('');
      loadIssue();
      alert('이슈가 거절되었습니다');
    } else {
      alert(response.error || '이슈 거절에 실패했습니다');
    }
  };

  const handleAddCommit = async (e: Event) => {
    e.preventDefault();

    if (!commitUrl.trim()) {
      alert('커밋 URL 또는 커밋 ID를 입력해주세요');
      return;
    }

    const input = commitUrl.trim();

    // Validate: either full URL or commit hash
    const urlPattern = /^https?:\/\/[^/]+\/[^/]+\/[^/]+\/commit\/[a-f0-9]+$/i;
    const hashPattern = /^[a-f0-9]{7,40}$/i;

    if (!urlPattern.test(input) && !hashPattern.test(input)) {
      alert('올바른 형식이 아닙니다\n- 커밋 URL: http://domain.com/owner/repo/commit/hash\n- 커밋 ID: a1b2c3d (7-40자)');
      return;
    }

    setAddingCommit(true);

    const response = await api.post<any>(`/api/commits/${id}/add`, {
      commit_url_or_hash: input,
    });

    setAddingCommit(false);

    if (response.success) {
      setCommitUrl('');
      setShowAddCommitForm(false);
      loadCommits();
      alert('커밋이 추가되었습니다');
    } else {
      if (response.error?.includes('409')) {
        alert('이미 연결된 커밋입니다');
      } else if (response.error?.includes('Invalid Gitea commit URL')) {
        alert('올바른 Gitea 커밋 URL이 아닙니다');
      } else if (response.error?.includes('Failed to fetch commit')) {
        alert('Gitea에서 커밋 정보를 가져올 수 없습니다\nURL과 커밋 해시를 확인해주세요');
      } else {
        alert(response.error || '커밋 추가에 실패했습니다');
      }
    }
  };

  // Drag and drop handlers for images
  const handleImageDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImage(true);
  };

  const handleImageDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImage(false);
  };

  const handleImageDrop = async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImage(false);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // Filter for image files only
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      alert('이미지 파일만 업로드할 수 있습니다');
      return;
    }

    // Create FileList-like object
    const dt = new DataTransfer();
    imageFiles.forEach(file => dt.items.add(file));

    setUploadingImage(true);

    const formData = new FormData();
    imageFiles.forEach(file => formData.append('images', file));

    const response = await api.post<any>(`/api/issues/${id}/images`, formData);

    setUploadingImage(false);

    if (response.success) {
      loadImages();
      alert(`${imageFiles.length}개의 이미지가 업로드되었습니다`);
    } else {
      alert(response.error || '이미지 업로드에 실패했습니다');
    }
  };

  // Clipboard paste handler for images
  const handleImagePaste = async (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length === 0) return;

    e.preventDefault();
    setUploadingImage(true);

    const formData = new FormData();
    imageFiles.forEach(file => formData.append('images', file));

    const response = await api.post<any>(`/api/issues/${id}/images`, formData);

    setUploadingImage(false);

    if (response.success) {
      loadImages();
      alert(`${imageFiles.length}개의 이미지가 업로드되었습니다`);
    } else {
      alert(response.error || '이미지 업로드에 실패했습니다');
    }
  };

  // Drag and drop handlers for files (edit modal)
  const handleFileDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  };

  const handleFileDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
  };

  const handleFileDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      setEditAttachments(files);
    }
  };

  // Drag and drop handlers for comment images
  const handleCommentImageDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCommentImage(true);
  };

  const handleCommentImageDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCommentImage(false);
  };

  const handleCommentImageDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCommentImage(false);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // Filter for image files only
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      alert('이미지 파일만 업로드할 수 있습니다');
      return;
    }

    // Create FileList-like object
    const dt = new DataTransfer();
    imageFiles.forEach(file => dt.items.add(file));
    setCommentImages(dt.files);
  };

  // Clipboard paste handler for comment images
  const handleCommentImagePaste = (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length === 0) return;

    e.preventDefault();

    // Create FileList-like object
    const dt = new DataTransfer();
    imageFiles.forEach(file => dt.items.add(file));
    setCommentImages(dt.files);
  };

  const canRejectIssue = (): boolean => {
    if (!issue || !userRole) return false;
    // PM이나 Developer만 거절 가능
    if (userRole !== 'pm' && userRole !== 'developer') return false;
    // 자신이 작성한 이슈는 거절 불가 (관리자 제외)
    if (issue.reporter_id === user.value?.id && !user.value?.is_admin) return false;
    // 이미 거절되었거나 해결된 이슈는 거절 불가
    return issue.status !== 'rejected' && issue.status !== 'resolved';
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      review: 'bg-purple-100 text-purple-800',
      on_hold: 'bg-orange-100 text-orange-800',
      rejected: 'bg-red-600 text-white font-semibold',
      resolved: 'bg-green-100 text-green-800',
      updated: 'bg-cyan-100 text-cyan-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      critical: 'bg-red-100 text-red-600',
    };
    return colors[priority] || 'bg-gray-100 text-gray-600';
  };

  if (!isAuthenticated.value) return null;

  return (
    <div
      class="min-h-screen bg-gray-100"
      onDragOver={(e: DragEvent) => e.preventDefault()}
      onDrop={(e: DragEvent) => e.preventDefault()}
    >
      <header class="bg-white shadow">
        <div class="mx-auto max-w-7xl px-4 py-4">
          <button
            onClick={() => issue && route(`/projects/${issue.project_id}`)}
            class="mb-2 text-sm text-blue-600"
          >
            ← 프로젝트로 돌아가기
          </button>
          {issue && (
            <div class="flex items-center justify-between">
              <div>
                <h1 class="text-xl font-bold">
                  <span class="font-mono text-blue-600">{issue.issue_number}</span>
                </h1>
                <h2 class="text-lg">{issue.title}</h2>
              </div>
              <div class="flex gap-2 items-center">
                <span class={`rounded px-3 py-1 text-sm ${getStatusBadge(issue.status)}`}>
                  {getStatusLabel(issue.status)}
                </span>
                <span class={`rounded px-3 py-1 text-sm ${getPriorityBadge(issue.priority)}`}>
                  {getPriorityLabel(issue.priority)}
                </span>
                <button onClick={openEditModal} class="btn btn-secondary ml-2">
                  이슈 수정
                </button>
                {canRejectIssue() && (
                  <button onClick={() => setShowRejectModal(true)} class="btn btn-danger ml-2">
                    이슈 거절
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      <main class="mx-auto max-w-7xl px-4 py-8">
        {loading ? (
          <div class="text-center">로딩중...</div>
        ) : (
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div class="lg:col-span-2">
              {/* Description */}
              <div class="card mb-6">
                <h3 class="mb-4 text-lg font-semibold">설명</h3>
                <p class="whitespace-pre-wrap text-gray-700">
                  {issue?.description || '설명이 없습니다'}
                </p>
              </div>

              {/* Rejection Reason */}
              {issue?.rejected_at && issue?.rejection_reason && (
                <div class="card mb-6 bg-red-50 border-red-200">
                  <h3 class="mb-2 text-lg font-semibold text-red-800">거절 사유</h3>
                  <p class="text-gray-700 mb-2">{issue.rejection_reason}</p>
                  <p class="text-sm text-gray-500">
                    거절자: {issue.rejected_by?.name} • {new Date(issue.rejected_at).toLocaleString()}
                  </p>
                </div>
              )}

              {/* Images */}
              <div class="card mb-6">
                <div class="mb-4 flex items-center justify-between">
                  <h3 class="text-lg font-semibold">이미지 ({images.length})</h3>
                  <label class="btn btn-secondary cursor-pointer">
                    {uploadingImage ? '업로드 중...' : '이미지 업로드'}
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      class="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                </div>

                {/* Drag and Drop Zone */}
                <div
                  tabIndex={0}
                  onDragOver={handleImageDragOver}
                  onDragLeave={handleImageDragLeave}
                  onDrop={handleImageDrop}
                  onPaste={handleImagePaste as any}
                  class={`mb-4 border-2 border-dashed rounded-lg text-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDraggingImage
                      ? 'border-blue-600 bg-blue-100 p-16 scale-105 shadow-lg'
                      : 'border-gray-300 bg-gray-50 hover:border-gray-400 p-8'
                  }`}
                >
                  {isDraggingImage ? (
                    <div class="animate-pulse">
                      <div class="text-6xl mb-4">📥</div>
                      <p class="text-lg font-semibold text-blue-700">
                        여기에 이미지를 놓으세요
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p class="text-sm text-gray-600">
                        📷 이미지를 드래그하여 놓거나, 여기 클릭 후 Ctrl+V로 붙여넣으세요
                      </p>
                      <p class="text-xs text-gray-400 mt-1">
                        스크린샷을 찍고 (Win+Shift+S) 바로 붙여넣을 수 있습니다
                      </p>
                    </div>
                  )}
                </div>

                <div class="grid grid-cols-2 gap-4 md:grid-cols-3">
                  {images.map((img) => {
                    const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8100';
                    return (
                      <div key={img.id} class="relative group aspect-square overflow-hidden rounded border">
                        <button
                          onClick={() => setSelectedImage(`${API_URL}${img.image_path}`)}
                          class="w-full h-full hover:opacity-75 cursor-pointer"
                        >
                          <img
                            src={`${API_URL}${img.image_path}`}
                            alt="Issue attachment"
                            class="h-full w-full object-cover"
                          />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(img.id);
                          }}
                          class="absolute top-2 right-2 bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                          title="이미지 삭제"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
                {images.length === 0 && (
                  <p class="text-center text-gray-500 mt-4">첨부된 이미지가 없습니다</p>
                )}
              </div>

              {/* Comments */}
              <div class="card">
                <h3 class="mb-4 text-lg font-semibold">댓글 ({comments.length})</h3>
                <div class="mb-4 space-y-4">
                  {comments.map((comment) => {
                    const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8100';
                    return (
                      <div key={comment.id} class="border-b pb-4 last:border-b-0">
                        <div class="mb-2 flex items-center justify-between">
                          <span class="font-medium">{comment.user_name}</span>
                          <span class="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p class="whitespace-pre-wrap text-gray-700 mb-3">{comment.content}</p>

                        {/* Comment Images */}
                        {comment.images && comment.images.length > 0 && (
                          <div class="grid grid-cols-2 gap-2 md:grid-cols-3">
                            {comment.images.map((img) => (
                              <button
                                key={img.id}
                                onClick={() => setSelectedImage(`${API_URL}${img.image_path}`)}
                                class="aspect-square overflow-hidden rounded border hover:opacity-75 cursor-pointer"
                              >
                                <img
                                  src={`${API_URL}${img.image_path}`}
                                  alt="Comment attachment"
                                  class="h-full w-full object-cover"
                                />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {comments.length === 0 && (
                    <p class="text-center text-gray-500">아직 댓글이 없습니다</p>
                  )}
                </div>

                <form onSubmit={handleAddComment}>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText((e.target as HTMLTextAreaElement).value)}
                    class="input w-full"
                    rows={3}
                    placeholder="댓글을 입력하세요..."
                  />
                  <div class="mb-2 mt-2">
                    <label class="mb-1 block text-sm font-medium">이미지 (선택사항)</label>

                    {/* Comment Image Drag and Drop Zone */}
                    <div
                      tabIndex={0}
                      onDragOver={handleCommentImageDragOver}
                      onDragLeave={handleCommentImageDragLeave}
                      onDrop={handleCommentImageDrop}
                      onPaste={handleCommentImagePaste as any}
                      class={`mb-2 border-2 border-dashed rounded-lg text-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDraggingCommentImage
                          ? 'border-blue-600 bg-blue-100 p-12 scale-105 shadow-lg'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400 p-6'
                      }`}
                    >
                      {isDraggingCommentImage ? (
                        <div class="animate-pulse">
                          <div class="text-5xl mb-3">📥</div>
                          <p class="text-base font-semibold text-blue-700">
                            여기에 이미지를 놓으세요
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p class="text-sm text-gray-600">
                            📷 이미지를 드래그하여 놓거나, 클릭 후 Ctrl+V로 붙여넣으세요
                          </p>
                          <p class="text-xs text-gray-400 mt-1">
                            {commentImages && commentImages.length > 0
                              ? `${commentImages.length}개 선택됨`
                              : '여러 이미지를 선택할 수 있습니다'}
                          </p>
                        </div>
                      )}
                    </div>

                    <input
                      id="comment-images"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => setCommentImages((e.target as HTMLInputElement).files)}
                      class="input w-full"
                    />
                  </div>
                  <button type="submit" class="btn btn-primary mt-2">
                    댓글 작성
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div class="lg:col-span-1">
              <div class="card">
                <h3 class="mb-4 text-lg font-semibold">상세 정보</h3>
                <dl class="space-y-3">
                  <div>
                    <dt class="text-sm font-medium text-gray-600">보고자</dt>
                    <dd class="text-sm">{issue?.reporter_name}</dd>
                  </div>
                  <div>
                    <dt class="text-sm font-medium text-gray-600">담당자</dt>
                    <dd class="text-sm">{issue?.assignee_name || '미할당'}</dd>
                  </div>
                  <div>
                    <dt class="text-sm font-medium text-gray-600">생성일</dt>
                    <dd class="text-sm">
                      {issue?.created_at && new Date(issue.created_at).toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt class="text-sm font-medium text-gray-600">수정일</dt>
                    <dd class="text-sm">
                      {issue?.updated_at && new Date(issue.updated_at).toLocaleString()}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Linked Issues */}
              {linkedIssues.length > 0 && (
                <div class="card mt-4">
                  <h3 class="mb-4 text-lg font-semibold">연결된 이슈 ({linkedIssues.length})</h3>
                  <div class="space-y-2">
                    {linkedIssues.map((linkedIssue) => (
                      <button
                        key={linkedIssue.id}
                        onClick={() => route(`/issues/${linkedIssue.id}`)}
                        class="w-full text-left p-3 border rounded hover:bg-gray-50 transition-colors"
                      >
                        <p class="text-sm font-medium text-blue-600 font-mono">
                          {linkedIssue.issue_number}
                        </p>
                        <p class="text-sm text-gray-700 line-clamp-2">
                          {linkedIssue.title}
                        </p>
                        <p class="text-xs text-gray-500 mt-1">
                          {getStatusLabel(linkedIssue.status)}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* File Attachments */}
              <div class="card mt-4">
                <h3 class="mb-4 text-lg font-semibold">첨부 파일</h3>

                {/* Upload */}
                <div class="mb-4">
                  <label class="btn btn-secondary cursor-pointer">
                    <input
                      type="file"
                      accept=".xls,.xlsx,.txt,.md,.zip"
                      onChange={handleFileUpload}
                      class="hidden"
                    />
                    파일 업로드
                  </label>
                  <p class="mt-1 text-xs text-gray-500">
                    지원 형식: Excel (.xls, .xlsx), 텍스트 (.txt, .md), ZIP (.zip)
                  </p>
                </div>

                {/* List */}
                {attachments.length > 0 && (
                  <div class="space-y-2">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        class="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                      >
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium text-gray-900 truncate">
                            {attachment.file_name}
                          </p>
                          <p class="text-xs text-gray-500">
                            {(attachment.file_size / 1024).toFixed(2)} KB · {new Date(attachment.uploaded_at).toLocaleString()}
                          </p>
                        </div>
                        <div class="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleFileDownload(attachment.id, attachment.file_name)}
                            class="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            다운로드
                          </button>
                          <button
                            onClick={() => handleFileDelete(attachment.id)}
                            class="text-red-600 hover:text-red-800 text-sm"
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {attachments.length === 0 && (
                  <p class="text-sm text-gray-500">첨부된 파일이 없습니다.</p>
                )}
              </div>

              {/* Linked Commits - Hidden for QA */}
              {userRole !== 'qa' && (
                <div class="card mt-4">
                  <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold">연결된 커밋 ({commits.length})</h3>
                    {!showAddCommitForm && (
                      <button
                        onClick={() => setShowAddCommitForm(true)}
                        class="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        커밋 추가
                      </button>
                    )}
                  </div>

                  {/* Add Commit Form */}
                  {showAddCommitForm && (
                    <form onSubmit={handleAddCommit} class="mb-4 p-4 bg-gray-50 rounded-lg">
                      <div class="space-y-3">
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">
                            Gitea 커밋 URL
                          </label>
                          <input
                            type="text"
                            value={commitUrl}
                            onInput={(e) => setCommitUrl((e.target as HTMLInputElement).value)}
                            placeholder="http://domain.com/owner/repo/commit/hash"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={addingCommit}
                          />
                          <p class="mt-1 text-xs text-gray-500">
                            예: http://feelinglucky.synology.me:3052/coinspace/coinspace_production/commit/a4c333e
                          </p>
                        </div>
                        <div class="flex gap-2">
                          <button
                            type="submit"
                            disabled={addingCommit}
                            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {addingCommit ? '추가 중...' : '추가'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddCommitForm(false);
                              setCommitUrl('');
                            }}
                            disabled={addingCommit}
                            class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                          >
                            취소
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Commits List */}
                  {commits.length > 0 ? (
                    <div class="space-y-3">
                      {commits.map((commit) => (
                        <div key={commit.id} class="border-b pb-3 last:border-b-0">
                          <p class="text-sm font-medium text-gray-900 line-clamp-2">
                            {commit.message}
                          </p>
                          <div class="mt-1 space-y-1 text-xs text-gray-500">
                            {commit.commit_url ? (
                              <a
                                href={commit.commit_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                class="font-mono text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {commit.commit_hash.substring(0, 8)}
                              </a>
                            ) : (
                              <div class="font-mono">{commit.commit_hash.substring(0, 8)}</div>
                            )}
                            <div>{commit.author}</div>
                            <div>{new Date(commit.committed_at).toLocaleString()}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    !showAddCommitForm && (
                      <p class="text-sm text-gray-500">연결된 커밋이 없습니다.</p>
                    )
                  )}
                </div>
              )}

              {/* Linked Wiki Pages */}
              {linkedWikiPages.length > 0 && (
                <div class="card mt-4">
                  <h3 class="text-lg font-semibold mb-4">연결된 문서 ({linkedWikiPages.length})</h3>
                  <div class="space-y-3">
                    {linkedWikiPages.map((wiki) => (
                      <div
                        key={wiki.id}
                        class="border-b pb-3 last:border-b-0 hover:bg-gray-50 p-2 rounded cursor-pointer"
                        onClick={() => {
                          if (issue?.project_id) {
                            route(`/projects/${issue.project_id}?tab=wiki`);
                          }
                        }}
                      >
                        <h4 class="font-medium text-blue-600 hover:text-blue-800">
                          {wiki.title}
                        </h4>
                        {wiki.content && (
                          <p class="mt-1 text-sm text-gray-600 line-clamp-2">
                            {wiki.content.substring(0, 150)}
                            {wiki.content.length > 150 && '...'}
                          </p>
                        )}
                        <div class="mt-1 flex items-center gap-3 text-xs text-gray-500">
                          <span>생성: {new Date(wiki.created_at).toLocaleDateString()}</span>
                          {wiki.updated_at && (
                            <span>수정: {new Date(wiki.updated_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            class="absolute top-4 right-4 text-white text-4xl font-bold hover:text-gray-300 z-10"
            aria-label="Close"
          >
            ×
          </button>
          <div class="relative max-w-7xl max-h-screen p-4">
            <img
              src={selectedImage}
              alt="Full size"
              class="max-w-full max-h-screen object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Reject Issue Modal */}
      {showRejectModal && (
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div class="card w-full max-w-lg">
            <h2 class="mb-4 text-xl font-bold text-red-600">이슈 거절</h2>
            <form onSubmit={handleRejectIssue}>
              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">거절 사유 *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason((e.target as HTMLTextAreaElement).value)}
                  class="input w-full"
                  rows={4}
                  placeholder="이슈를 거절하는 사유를 입력해주세요..."
                  required
                />
                <p class="mt-1 text-xs text-gray-500">
                  거절 사유는 필수입니다. 자신이 작성한 이슈는 거절할 수 없습니다 (관리자 제외).
                </p>
              </div>

              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                  }}
                  class="btn btn-secondary"
                >
                  취소
                </button>
                <button type="submit" class="btn btn-danger">
                  거절하기
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Issue Modal */}
      {showEditModal && (
        <div class="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
          <div class="flex min-h-full items-center justify-center p-4">
            <div class="card w-full max-w-lg">
            <h2 class="mb-4 text-xl font-bold">이슈 수정</h2>
            <form onSubmit={handleEditIssue}>
              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">제목 *</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  required
                />
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">설명</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription((e.target as HTMLTextAreaElement).value)}
                  class="input w-full"
                  rows={4}
                />
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">상태</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus((e.target as HTMLSelectElement).value)}
                  class="input w-full"
                >
                  <option value="rejected">거절됨</option>
                  <option value="new">신규</option>
                  <option value="review">검토중</option>
                  <option value="in_progress">진행중</option>
                  <option value="on_hold">보류됨</option>
                  <option value="resolved">해결됨</option>
                  <option value="updated">업데이트완료</option>
                </select>
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">우선순위</label>
                <select
                  value={editPriority}
                  onChange={(e) => setEditPriority((e.target as HTMLSelectElement).value)}
                  class="input w-full"
                >
                  <option value="low">낮음</option>
                  <option value="medium">보통</option>
                  <option value="high">높음</option>
                  <option value="critical">긴급</option>
                </select>
              </div>

              {userRole !== 'qa' && (
                <div class="mb-4">
                  <label class="mb-1 block text-sm font-medium">담당자</label>
                  <select
                    value={editAssigneeId}
                    onChange={(e) => setEditAssigneeId((e.target as HTMLSelectElement).value)}
                    class="input w-full"
                  >
                    <option value="">미할당</option>
                    {projectMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} ({member.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {issue && (
                <div class="mb-4">
                  <IssueLinkSelector
                    projectId={issue.project_id}
                    selectedIssues={editLinkedIssues}
                    onChange={setEditLinkedIssues}
                    currentIssueId={issue.id}
                  />
                </div>
              )}

              {userRole !== 'qa' && (
                <div class="mb-4">
                  <label class="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editIsHidden}
                      onChange={(e) => setEditIsHidden((e.target as HTMLInputElement).checked)}
                      class="rounded"
                    />
                    <span>🔒 히든 이슈 (QA에게 숨김)</span>
                  </label>
                </div>
              )}

              <div class="mb-6">
                <label class="mb-1 block text-sm font-medium">파일 첨부</label>

                {/* File Drag and Drop Zone */}
                <div
                  onDragOver={handleFileDragOver}
                  onDragLeave={handleFileDragLeave}
                  onDrop={handleFileDrop}
                  class={`mb-2 border-2 border-dashed rounded-lg text-center transition-all duration-200 ${
                    isDraggingFile
                      ? 'border-blue-600 bg-blue-100 p-12 scale-105 shadow-lg'
                      : 'border-gray-300 bg-gray-50 p-6'
                  }`}
                >
                  {isDraggingFile ? (
                    <div class="animate-pulse">
                      <div class="text-5xl mb-3">📂</div>
                      <p class="text-base font-semibold text-blue-700">
                        여기에 파일을 놓으세요
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p class="text-sm text-gray-600">
                        📁 파일을 드래그하여 놓거나 아래 버튼으로 선택하세요
                      </p>
                      <p class="text-xs text-gray-400 mt-1">
                        Excel, Text, Markdown, ZIP 파일
                      </p>
                    </div>
                  )}
                </div>

                <input
                  type="file"
                  accept=".xls,.xlsx,.txt,.md,.zip"
                  multiple
                  onChange={(e) => setEditAttachments((e.target as HTMLInputElement).files)}
                  class="input w-full"
                />
                <p class="mt-1 text-xs text-gray-500">
                  여러 파일을 선택할 수 있습니다 (Excel, Text, Markdown, ZIP)
                </p>
              </div>

              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  class="btn btn-secondary"
                >
                  취소
                </button>
                <button type="submit" class="btn btn-primary">
                  수정하기
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
